// @flow
import React, { PureComponent } from 'react';
import { Link } from 'react-router-dom';

import DropdownRadioInput from 'components/fragments/input/DropdownRadioInput';
import BackgroundImage from 'containers/fragments/BackgroundImageContainer';

import { dynamicClassName } from 'utils/dynamicClassName';
import { formatFullDate } from 'utils/dateUtils';
import { getDateFromString } from 'utils/dateUtils';

import type { RencontreArticleType } from 'types/Rencontre';
import type { ArticleType, CategoryArticleType } from 'types/Article';

type Props = {
  news: ArticleType[],
  categoriesArticle: CategoryArticleType[],
};

type ComponentState = {
  catId: number,
  maxNews: number,
};

class BillboardDirect extends PureComponent<Props, ComponentState> {
  state: ComponentState = {
    catId: 0,
    maxNews: 2,
  };

  componentDidMount() {
    this.updateDimensions();
    window.addEventListener('resize', this.updateDimensions.bind(this));
  }

  componentDidUpdate() {
    this.updateDimensions();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions.bind(this));
  }

  updateDimensions() {
    const { maxNews } = this.state;
    if (window.innerWidth < 840 && maxNews !== 2) {
      this.setState({ maxNews: 2 });
    }
    if (window.innerWidth >= 840 && maxNews !== 2) {
      this.setState({ maxNews: 2 });
    }
  }

  handleChange = (value: any) => {
    this.setState({
      catId: value
    });
  };

  renderScoreRow = (rencontre_details: RencontreArticleType) => {
    if (rencontre_details) {
      const {
        competition,
        journee,
        localStructure,
        visiteurStructure,
        pointsStructureLocale,
        pointsStructureVisiteur
      } = rencontre_details;

      return (
        <>
          <div className="score">
            <p className="ft-h6 mb-2 mb-md-1 mb-lg-2 ft-up match-context">
              {competition.nom} - {journee}
            </p>
            <div className="match ">
              <div className="match__club">
                <p>
                  <span>
                    {localStructure.nom}
                  </span>
                  <img
                    src={localStructure.embleme}
                    alt={`Logo ${localStructure.nom}`}
                    width="24"
                    height="24" />
                </p>
              </div>
              <div className="match__info">
                <p className="match__score">
                  <span>{pointsStructureLocale}</span>
                  <span className="match__colon">-</span>
                  <span className="ft-700">{pointsStructureVisiteur}</span>
                </p>
              </div>
              <div className="match__club">
                <p className="ft-700">
                  <img
                    src={visiteurStructure.embleme}
                    alt={`Logo ${visiteurStructure.nom}`}
                    width="24"
                    height="24" />
                  <span>
                    {visiteurStructure.nom}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </>
      );
    }
    return null;
  };

  renderRow = (news: ArticleType) => {
    const {
      illustration,
      title,
      label,
      slug,
      link,
      srcset,
      rencontre_details,
      rencontre_liee,
    } = news;

    const date = getDateFromString(news.date);
    const isMatchLinked = rencontre_details && rencontre_liee;

    const linkClassname = dynamicClassName('news news--row');
    isMatchLinked && linkClassname.add('news--score');

    return (
      <Link key={slug}
        to={{
          pathname: link
        }}
        title={`Lire la suite de l'article : ${title}`}
        role="button"
        tabIndex={0}
        className={linkClassname.build()}>
        <article>
          <BackgroundImage
            className="news__thumbnail img-blue"
            illustration={illustration}
            srcset={srcset}
            forceState={'thumbnail'}
            baliseImg
            bypassClubPlaceholder
          />
          <div className="news__title">
            <span className="ft-h5">{title}</span>
            <p className="ft-h6">
              <span className="news__cat ft-up">{label.name}</span>
              <span className="news__date">{formatFullDate(date)}</span>
            </p>
          </div>
          {rencontre_details && this.renderScoreRow(rencontre_details)}
        </article>
      </Link>
    );
  };

  renderNews() {
    const { news, categoriesArticle } = this.props;
    const { catId } = this.state;
    const count = 2;
    if (catId === 0) {
      return news.map<any>((news, index) => {
        if (index > count) {
          return null;
        }
        return this.renderRow(news);
      });
    } else {
      return categoriesArticle.map<any>((cat, index) => {
        if (cat.id === catId) {
          return cat.items.filter((article) => {
            return article.link !== news[0].link;
          }).map((news, index) => {
            if (index > count) {
              return null;
            }
            return this.renderRow(news);
          });
        }
        return null;
      });
    }
  }

  render() {
    const { news, categoriesArticle } = this.props;
    const { maxNews, catId } = this.state;
    const categories_options = categoriesArticle.map((cat) => {return {label: cat.name, value: cat.id};});
    categories_options.unshift({label: maxNews === 4 ? 'Toutes les catégories' : 'Toutes catégories', value: 0});
    categories_options.map((option, index) => {
      if (option.label.length > 25) {
        option.label = option.label.substring(0, 24)+ '...';
      }
      return null;
    });

    if (news.length === 0) {
      return null;
    }
    
    let dropdownLabel = categories_options[0].label;
    if (catId) {
      const dropdownLabels = categories_options.filter((cat) => {
        return cat.value === catId;
      });
      if (dropdownLabels.length > 0) {
        dropdownLabel = dropdownLabels[0].label;
      }
    }
    return (
      <div className='card--news-direct box box--white full-width is-home'>
        <div className='card__head'>
          <h3 className='ft-h3'>En direct</h3>
          <DropdownRadioInput
            options={categories_options}
            inputChanged={this.handleChange}
            label={dropdownLabel}
            radio={true}
            maj={true}
            title={true}
            noBorder={true}
          />
        </div>
        <div className="card__body">
          {this.renderNews()}
        </div>
        <div className='card__footer'>
          <Link
            to={{
              pathname: 'actualites'
            }}
            role="button"
            tabIndex={0}
            className='link-chevron'
          >
            <span>Voir toutes les actualités</span> <i className='icon icon-chevron-right' />
          </Link>
        </div>
      </div>
    );
  }
};

export default BillboardDirect;
